import { Injectable } from "@angular/core";
import { DataService } from "@app/core/services/data.service";
import { DefaultWidgetCampaign, LocationWidgetSettings, QRCodeSettings, SeeMenuWidgetSettings } from "@app/models";
// fixme: @app/pages/components should not be used in services
import { AddPromoWidgetSettings } from "@app/pages/components/widget-settings/add-promo-widget-settings/add-promo-widget-settings";
import { AddToWaitlistWidgetSettings } from "@app/pages/components/widget-settings/add-to-waitlist-widget-settings/add-to-waitlist-widget-settings";
import { AnniversaryClubWidgetSettings } from "@app/pages/components/widget-settings/anniversary-club-widget-settings/anniversary-club-widget-settings";
import { ConnectSocialNetworkWidgetSettings } from "@app/pages/components/widget-settings/connect-social-network-widget-settings/connect-social-network-widget-settings";
import { DownloadMobileAppWidgetSettings } from "@app/pages/components/widget-settings/download-mobile-app-widget-settings/download-mobile-app-widget-settings";
import { DownloadPromotionMobileAppWidgetSettings } from "@app/pages/components/widget-settings/download-promotion-mobile-app-widget-settings/download-promotion-mobile-app-widget-settings";
import { EnrollLoyaltyWidgetSettings } from "@app/pages/components/widget-settings/enroll-loyalty-widget-settings/enroll-loyalty-widget-settings";
import { ReferralProgramWidgetSettings } from "@app/pages/components/widget-settings/referral-program-widget-settings/referral-program-widget-settings";
import { RequestReviewWidgetSettings } from "@app/pages/components/widget-settings/request-review-widget-settings/review-request-widget-settings";
import { ScheduleAppointmentWidgetSettings } from "@app/pages/components/widget-settings/schedule-appointment-widget-settings/schedule-appointment-widget-settings";
import { SendGiftCardWidgetSettings } from "@app/pages/components/widget-settings/send-gift-card-widget-settings/send-gift-card-widget-settings";
import { SendPaymentLinkWidgetSettings } from "@app/pages/components/widget-settings/send-payment-link-widget-settings/send-payment-link-widget-settings.component";
import { SendSurveyWidgetSettings } from "@app/pages/components/widget-settings/send-survey-widget-settings/send-survey-widget-settings";
import { SetReminderWidgetSettings } from "@app/pages/components/widget-settings/set-reminder-widget-settings/set-reminder-widget-settings";
import { SmartPageWidgetSettings } from "@app/pages/components/widget-settings/smart-page-widget-settings/smart-page-widget-settings";
import { TranslocoService } from "@ngneat/transloco";
import {
  Campaign,
  CreateCustomWidgetPostDto,
  LocationWidgetSettingsGetDto,
  LocationWidgetSettingsMode,
  LocationWithDefaultWidgetGetDto,
  WidgetManagementService
} from "clearline-api";
import { WidgetType } from "clearline-common";
import { forkJoin, Observable } from "rxjs";
import { map } from "rxjs/operators";

@Injectable({
  providedIn: "root"
})
export class WidgetService {
  constructor(private http: DataService, private translateService: TranslocoService, private widgetApiService: WidgetManagementService) {
    this.initWidgetTranslations();
    this.initWidgetIconsMap();
  }

  private serviceUrl = "widgetManagement";
  private translateWidgetMap = new Map<WidgetType, string>();
  private widgetIconsMap = new Map<WidgetType, string>();

  get defaultConfiguredWidgets() {
    return [
      WidgetType.IssueCoupon,
      WidgetType.RequestReview,
      WidgetType.DownloadMobileApp,
      WidgetType.ConnectSocialNetwork,
      WidgetType.OneTimePayment,
      WidgetType.SeeMenu
    ];
  }

  private widgetSettings:
    | RequestReviewWidgetSettings
    | SetReminderWidgetSettings
    | AddPromoWidgetSettings
    | DownloadMobileAppWidgetSettings
    | DownloadPromotionMobileAppWidgetSettings
    | ConnectSocialNetworkWidgetSettings
    | SendPaymentLinkWidgetSettings
    | AnniversaryClubWidgetSettings
    | SendGiftCardWidgetSettings
    | SendSurveyWidgetSettings
    | AddToWaitlistWidgetSettings
    | ReferralProgramWidgetSettings
    | ScheduleAppointmentWidgetSettings
    | EnrollLoyaltyWidgetSettings
    | SmartPageWidgetSettings
    | SeeMenuWidgetSettings;

  initWidgetTranslations() {
    return this.translateService.selectTranslateObject("widgets").subscribe((t) => {
      this.translateWidgetMap.set(WidgetType.IssueCoupon, t.issueCoupon.title);
      this.translateWidgetMap.set(WidgetType.ValidateCoupon, t.validateCoupon.title);
      this.translateWidgetMap.set(WidgetType.EnrollToLoyalty, t.enrollLoyalty.title);
      this.translateWidgetMap.set(WidgetType.IssuePointsOrStamps, t.issuePoints.title);
      this.translateWidgetMap.set(WidgetType.RequestReview, t.requestReview.title);
      this.translateWidgetMap.set(WidgetType.SetReminder, t.setReminder.title);
      this.translateWidgetMap.set(WidgetType.AddPromo, t.addPromo.title);
      this.translateWidgetMap.set(WidgetType.DownloadMobileApp, t.downloadMobileApp.title);
      this.translateWidgetMap.set(WidgetType.DownloadPromotionMobileApp, t.downloadPromotionMobileApp.title);
      this.translateWidgetMap.set(WidgetType.ConnectSocialNetwork, t.connectSocialMedia.title);
      this.translateWidgetMap.set(WidgetType.OneTimePayment, t.sendPaymentLink.title);
      this.translateWidgetMap.set(WidgetType.ScheduleAppointment, t.scheduleAppointment.title);
      this.translateWidgetMap.set(WidgetType.AddToWaitlist, t.waitlist.title);
      this.translateWidgetMap.set(WidgetType.SendSurvey, t.sendSurvey.title);
      this.translateWidgetMap.set(WidgetType.SendGiftCard, t.sendGiftCard.title);
      this.translateWidgetMap.set(WidgetType.AnniversaryClub, t.anniversaryClub.title);
      this.translateWidgetMap.set(WidgetType.AddToReferralProgram, t.referralProgram.title);
      this.translateWidgetMap.set(WidgetType.SmartPage, t.smartPage.title);
      this.translateWidgetMap.set(WidgetType.SeeMenu, t.seeMenu.widgetTitle);
    });
  }

  /** @deprecated should use widgetName from Dto instead */
  getWidgetNameById(id: number) {
    return this.translateWidgetMap.get(id);
  }

  getWidgetsInThePlan(widgets: LocationWithDefaultWidgetGetDto[]): LocationWithDefaultWidgetGetDto[] {
    return widgets.filter((item) => !item.isOutOfThePlan);
  }

  getDefaultWidget(widgetId: number): Observable<Campaign> {
    return this.http.get(`${this.serviceUrl}/${widgetId}`).pipe(
      map((w: Campaign) => {
        w.name = this.translateWidgetMap.get(w.id) || w.name;
        return w;
      })
    );
  }

  getAllCampaigns(): Observable<Campaign[]> {
    return this.http.get(`${this.serviceUrl}/all`).pipe(
      map((wgs: Campaign[]) => {
        wgs.forEach((w) => (w.name = this.translateWidgetMap.get(w.id) || w.name));
        return wgs;
      })
    );
  }

  updateDefaultCampaign(campaign: Campaign) {
    return this.http.put(`${this.serviceUrl}/updateDefaultCampaign`, campaign);
  }

  getLocationWidgets(locationId: number): Observable<LocationWidgetSettings[]> {
    return this.http.get(`${this.serviceUrl}/locationCampaigns/${locationId}`);
  }

  getDefaultWidgetCampaign(widgetId: number): Observable<DefaultWidgetCampaign> {
    return this.http.get(`${this.serviceUrl}/campaign/${widgetId}`);
  }

  getClickableTemplateWidgets(): Observable<Campaign[]> {
    return this.http.get(`${this.serviceUrl}/clickableTemplate`);
  }

  updateLocationCampaign(campaign: LocationWidgetSettingsGetDto, mode = LocationWidgetSettingsMode.Cmc) {
    return this.widgetApiService.updateLocationCampaign(campaign, mode);
  }

  updateWidgetSettings(campaign: LocationWidgetSettingsGetDto, mode = LocationWidgetSettingsMode.Cmc, widgetId: number) {
    const name = JSON.parse(campaign.widgetSettingsJson || null)?.appName || null;
    const updateLocationCampaign$ = this.updateLocationCampaign(campaign, mode);

    if (!!name) {
      return forkJoin([updateLocationCampaign$, this.widgetApiService.updateCustomWidget({ name, widgetId })]);
    }

    return updateLocationCampaign$;
  }

  createCustomWidget(payload: CreateCustomWidgetPostDto) {
    return this.widgetApiService.createCustomWidget(payload);
  }

  deleteCustomWidget(id: number) {
    return this.widgetApiService.deleteCustomWidget(id);
  }

  getAllLocationDefaultWidgets(locationId: number, mode = LocationWidgetSettingsMode.Cmc): Observable<LocationWithDefaultWidgetGetDto[]> {
    return this.widgetApiService.getLocationDefaultWidgets(locationId, mode).pipe(
      map((widgetList: LocationWithDefaultWidgetGetDto[]) => {
        const widgets: LocationWithDefaultWidgetGetDto[] = widgetList.filter((x) => !x.isOutOfThePlan);

        return widgets.map((widget: LocationWithDefaultWidgetGetDto) => ({
          ...widget,
          locationWidgetSettings: {
            ...widget.locationWidgetSettings,
            widgetSettingsJson: widget.locationWidgetSettings.widgetSettingsJson || null
          },
          name: widget.widget.name
        }));
      })
    );
  }

  getQrSettings(featureId: WidgetType, ls: LocationWidgetSettings): QRCodeSettings {
    const widgetSettingsJson = ls.widgetSettingsJson;

    if (!widgetSettingsJson) {
      this.widgetSettings = null;
      return null;
    }

    switch (featureId) {
      case WidgetType.EnrollToLoyalty:
        this.widgetSettings = JSON.parse(widgetSettingsJson) as EnrollLoyaltyWidgetSettings;
        return { url: ls.landingUrl || this.widgetSettings.enrollLoyaltyLink, logo: "" } as QRCodeSettings;
      case WidgetType.RequestReview:
        this.widgetSettings = JSON.parse(widgetSettingsJson) as RequestReviewWidgetSettings;
        return { url: ls.landingUrl, logo: "" } as QRCodeSettings;
      case WidgetType.SetReminder:
        this.widgetSettings = JSON.parse(widgetSettingsJson) as SetReminderWidgetSettings;
        return { url: this.widgetSettings.setReminderUrl || ls.landingUrl, logo: "" } as QRCodeSettings;
      case WidgetType.AddPromo:
        this.widgetSettings = JSON.parse(widgetSettingsJson) as AddPromoWidgetSettings;
        return { url: this.widgetSettings.promoListUrl || ls.landingUrl, logo: "" } as QRCodeSettings;
      case WidgetType.DownloadMobileApp:
        this.widgetSettings = JSON.parse(widgetSettingsJson) as DownloadMobileAppWidgetSettings;
        return { url: ls.landingUrl, logo: "" } as QRCodeSettings;
      case WidgetType.DownloadPromotionMobileApp:
        this.widgetSettings = JSON.parse(widgetSettingsJson) as DownloadPromotionMobileAppWidgetSettings;
        return { url: ls.landingUrl, logo: "" } as QRCodeSettings;
      case WidgetType.ConnectSocialNetwork:
        this.widgetSettings = JSON.parse(widgetSettingsJson) as ConnectSocialNetworkWidgetSettings;
        return { url: ls.landingUrl, logo: "" } as QRCodeSettings;
      case WidgetType.OneTimePayment:
        this.widgetSettings = JSON.parse(widgetSettingsJson) as SendPaymentLinkWidgetSettings;
        return { url: ls.landingUrl, logo: "" } as QRCodeSettings;
      case WidgetType.AnniversaryClub:
        this.widgetSettings = JSON.parse(widgetSettingsJson) as AnniversaryClubWidgetSettings;
        return { url: this.widgetSettings.anniversaryClubUrl || ls.landingUrl, logo: "" } as QRCodeSettings;
      case WidgetType.SendGiftCard:
        this.widgetSettings = JSON.parse(widgetSettingsJson) as SendGiftCardWidgetSettings;
        return { url: ls.landingUrl || this.widgetSettings.sendGiftCardUrl, logo: "" } as QRCodeSettings;
      case WidgetType.SendSurvey:
        this.widgetSettings = JSON.parse(widgetSettingsJson) as SendSurveyWidgetSettings;
        return { url: ls.landingUrl || this.widgetSettings.surveyUrl, logo: "" } as QRCodeSettings;
      case WidgetType.AddToWaitlist:
        this.widgetSettings = JSON.parse(widgetSettingsJson) as AddToWaitlistWidgetSettings;
        return { url: ls.landingUrl || this.widgetSettings.addToWaitListUrl, logo: "" } as QRCodeSettings;
      case WidgetType.AddToReferralProgram:
        this.widgetSettings = JSON.parse(widgetSettingsJson) as ReferralProgramWidgetSettings;
        return { url: this.widgetSettings.referralUrl || ls.landingUrl, logo: "" } as QRCodeSettings;
      case WidgetType.ScheduleAppointment:
        this.widgetSettings = JSON.parse(widgetSettingsJson) as ScheduleAppointmentWidgetSettings;
        return { url: ls.landingUrl || this.widgetSettings.appointmentUrl, logo: "" } as QRCodeSettings;
      case WidgetType.SmartPage:
        this.widgetSettings = JSON.parse(widgetSettingsJson) as SmartPageWidgetSettings;
        return { url: ls.landingUrl, logo: "" } as QRCodeSettings;
      case WidgetType.SeeMenu:
        this.widgetSettings = JSON.parse(widgetSettingsJson) as SeeMenuWidgetSettings;
        return { url: ls.landingUrl || this.widgetSettings.redirectUrl, logo: "" } as QRCodeSettings;
      default:
        return null;
    }
  }

  getDeactiveSMSCampaignSettings(widgetId: WidgetType, ls: LocationWidgetSettings): boolean {
    const widgetSettingsJson = ls.widgetSettingsJson;
    if (!widgetSettingsJson) return null;
    switch (widgetId) {
      case WidgetType.SetReminder:
        this.widgetSettings = JSON.parse(widgetSettingsJson) as SetReminderWidgetSettings;
        return !this.widgetSettings.setReminderUrl || this.widgetSettings.setReminderUrl === null;
      case WidgetType.AddPromo:
        this.widgetSettings = JSON.parse(widgetSettingsJson) as AddPromoWidgetSettings;
        return !this.widgetSettings.promoListUrl || this.widgetSettings.promoListUrl === null;
      case WidgetType.AnniversaryClub:
        this.widgetSettings = JSON.parse(widgetSettingsJson) as AnniversaryClubWidgetSettings;
        return !this.widgetSettings.anniversaryClubUrl || this.widgetSettings.anniversaryClubUrl === null;
      default:
        return null;
    }
  }

  getWidgetIcon(id: number): string {
    return this.widgetIconsMap.get(id);
  }

  isWidgetEditableByManager(id: WidgetType): boolean {
    const noneEditableWidgets: WidgetType[] = [
      WidgetType.ValidateCoupon,
      WidgetType.IssuePointsOrStamps,
      WidgetType.DownloadPromotionMobileApp
    ];

    return !noneEditableWidgets.includes(id);
  }

  isInteractiveWidget(widget: LocationWithDefaultWidgetGetDto): boolean {
    const nonInteractiveWidgetTypes: WidgetType[] = [WidgetType.IssuePointsOrStamps, WidgetType.DownloadPromotionMobileApp];

    return !nonInteractiveWidgetTypes.includes(widget?.widget?.id);
  }

  hasWidgetCampaign(id: WidgetType): boolean {
    const nonCampaignWidgets: WidgetType[] = [WidgetType.ValidateCoupon, WidgetType.IssuePointsOrStamps];

    return !nonCampaignWidgets.includes(id);
  }

  getSortedWidgets(widgets: LocationWithDefaultWidgetGetDto[], configured: boolean = false): LocationWithDefaultWidgetGetDto[] {
    if (configured) {
      const smartPageWidgetIndex: number = widgets.findIndex((item: LocationWithDefaultWidgetGetDto) => {
        return item?.defaultWidgetSettings?.id === WidgetType.SmartPage;
      });

      if (smartPageWidgetIndex > 0) {
        const smartPageWidget: LocationWithDefaultWidgetGetDto = widgets[smartPageWidgetIndex];

        if (!smartPageWidget.locationWidgetSettings?.disabled) {
          const slicedList: LocationWithDefaultWidgetGetDto[] = this.getSlicedSortedWidgetList(widgets, smartPageWidgetIndex);

          widgets = [smartPageWidget].concat(slicedList);
        }
      }
    }

    return widgets;
  }

  private getSlicedSortedWidgetList(
    widgets: LocationWithDefaultWidgetGetDto[],
    indexForReplace: number
  ): LocationWithDefaultWidgetGetDto[] {
    const slicedListUntilGoal: LocationWithDefaultWidgetGetDto[] = widgets.slice(0, indexForReplace);
    const slicedListAfterGoal: LocationWithDefaultWidgetGetDto[] = widgets[indexForReplace + 1] ? widgets.slice(indexForReplace + 1) : [];

    return slicedListUntilGoal.concat(slicedListAfterGoal);
  }

  private initWidgetIconsMap() {
    this.widgetIconsMap.set(WidgetType.IssueCoupon, "ri-coupon-line");
    this.widgetIconsMap.set(WidgetType.ValidateCoupon, "ri-qr-scan-line");
    this.widgetIconsMap.set(WidgetType.EnrollToLoyalty, "ri-service-line");
    this.widgetIconsMap.set(WidgetType.IssuePointsOrStamps, "ri-hand-coin-line");
    this.widgetIconsMap.set(WidgetType.RequestReview, "ri-chat-forward-line");
    this.widgetIconsMap.set(WidgetType.SetReminder, "ri-alarm-line");
    this.widgetIconsMap.set(WidgetType.AddPromo, "ri-file-list-3-line");
    this.widgetIconsMap.set(WidgetType.DownloadMobileApp, "ri-smartphone-line");
    this.widgetIconsMap.set(WidgetType.DownloadPromotionMobileApp, "ri-mobile-download-line");
    this.widgetIconsMap.set(WidgetType.ConnectSocialNetwork, "ri-link");
    this.widgetIconsMap.set(WidgetType.OneTimePayment, "ri-refund-2-line");
    this.widgetIconsMap.set(WidgetType.ScheduleAppointment, "ri-calendar-todo-line");
    this.widgetIconsMap.set(WidgetType.AddToWaitlist, "ri-user-add-line");
    this.widgetIconsMap.set(WidgetType.SendSurvey, "ri-question-answer-line");
    this.widgetIconsMap.set(WidgetType.SendGiftCard, "ri-gift-2-line");
    this.widgetIconsMap.set(WidgetType.AnniversaryClub, "ri-cake-2-line");
    this.widgetIconsMap.set(WidgetType.AddToReferralProgram, "ri-medal-line");
    this.widgetIconsMap.set(WidgetType.SmartPage, "ri-settings-line");
    this.widgetIconsMap.set(WidgetType.SeeMenu, "ri-list-check-3");
  }
}
